<template>
    <div>
        <Breadcrumbs class="mb-3" />
        <page-title :text="$t('page_titles.locations_page_title')" />
        <div>
            <v-tabs v-model="selectedTab" data-cy="tabs" class="mb-4" @update:model-value="load">
                <v-tab v-for="tab in tabs" :key="tab.value + '-tab'" :data-cy="tab.value + '-tab'">
                    {{ tab.title }}
                </v-tab>
            </v-tabs>
            <v-tabs-window v-model="selectedTab">
                <v-tabs-window-item value="country">
                    <LocationsTable :items="tableItems" :is-loading="isLoading" location-type="country" />
                </v-tabs-window-item>
                <v-tabs-window-item value="city">
                    <LocationsTable :items="tableItems" :is-loading="isLoading" location-type="city">
                        <template v-slot:filters>
                            <LocationsFilters
                                v-model="filters"
                                :has-active-filters="hasActiveFilters"
                                location-type="city"
                                @reset="resetFilters"
                                @submit="load"
                            />
                        </template>
                    </LocationsTable>
                </v-tabs-window-item>
                <!--<v-tabs-window-item value="restricted_location">-->
                <!--    <LocationsTable-->
                <!--        :items="tableItems"-->
                <!--        :is-loading="isLoading"-->
                <!--        :hide-table="regionFilter === 'ALL'"-->
                <!--        location-type="restricted_location"-->
                <!--        @remove-location="removeFromTable($event)"-->
                <!--    >-->
                <!--        <template v-slot:filters>-->
                <!--            <RestrictedLocationsFilters-->
                <!--                v-model="regionFilter"-->
                <!--                :region-filter="regionFilter"-->
                <!--                @add-to-list="addToTable($event)"-->
                <!--            />-->
                <!--            <v-row class="mb-1">-->
                <!--                <v-col>-->
                <!--                    <v-btn-->
                <!--                        :loading="isSaving"-->
                <!--                        :disabled="isSaving"-->
                <!--                        size="small"-->
                <!--                        color="primary"-->
                <!--                        data-cy="save-changes-button"-->
                <!--                        class="ml-4"-->
                <!--                        @click="saveRestrictedLocation"-->
                <!--                    >-->
                <!--                        {{ $t('save_changes') }}-->
                <!--                    </v-btn>-->
                <!--                </v-col>-->
                <!--            </v-row>-->
                <!--        </template>-->
                <!--    </LocationsTable>-->
                <!--</v-window-item>-->
                <!--<v-window-item value="popular_destination"> popular_destinations </v-window-item>-->
                <v-tabs-window-item value="location_point">
                    <LocationsTable
                        :items="tableItems"
                        :is-loading="isLoading"
                        location-type="location_point"
                        allow-edit
                        allow-create
                        @load-locations="load"
                    >
                        <template v-slot:filters>
                            <LocationsFilters
                                v-model="filters"
                                :has-active-filters="hasActiveFilters"
                                location-type="location_point"
                                @reset="resetFilters"
                                @submit="load"
                            />
                        </template>
                    </LocationsTable>
                </v-tabs-window-item>
            </v-tabs-window>
        </div>
        <!--        <LocationPointDetailsModal v-model="locationDetailsModal" :data="selectedLocationPoint" @save="load" />-->
    </div>
</template>

<script setup lang="ts">
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import LocationsTable from '~/components/account/locations/LocationsTable.vue'
    import LocationPointDetailsModal from '~/components/account/locations/LocationPointDetailsModal.vue'
    import type {LocationPointType, LocationsFiltersType} from '~/utils/types/account/locations/types'
    import {initLocationPoint, initLocationsFilters} from '~/utils/types/account/locations/helpers'
    import LocationsFilters from '~/components/account/locations/LocationsFilters.vue'
    import PageTitle from '@/components/parts/PageTitle.vue'
    // import RestrictedLocationsFilters from '~/components/account/locations/RestrictedLocationsFilters.vue'
    import {isEqual} from '~/utils/helpers'

    const app = useNuxtApp()
    const {t} = useI18n()

    const tabs: ComputedRef<{title: string; value: string}[]> = computed(() => {
        const tabs: {title: string; value: string}[] = [
            {title: t('countries'), value: 'country'},
            {title: t('cities'), value: 'city'},
            // {title: t('restricted_destinations'), value: 'restricted_location'},
            // {title: t('popular_destinations'), value: 'popular_destination'},
            {title: t('location_points'), value: 'location_point'},
        ]

        return tabs
    })
    const isLoading = ref<boolean>(false)
    // const isSaving = ref<boolean>(false)
    const filters = ref<LocationsFiltersType>(initLocationsFilters())
    const hasActiveFilters: ComputedRef<boolean> = computed(() => !isEqual(initLocationsFilters(), filters.value))
    const selectedTab = ref<number>(0)
    const locationDetailsModal = ref<boolean>(false)
    const tableItems = ref<any[]>([])
    const regionFilter = ref<string>('')

    onMounted(async () => {
        await load()
    })

    function showLocationPointModal(location: LocationPointType): void {
        locationDetailsModal.value = true
    }

    function resetFilters(): void {
        filters.value = initLocationsFilters()
        load()
    }

    async function load(): Promise<void> {
        tableItems.value = []
        if (tabs.value[selectedTab.value].value === 'country') await loadCountries()
        else if (tabs.value[selectedTab.value].value === 'city') await loadCities()
        else if (tabs.value[selectedTab.value].value === 'location_point') await loadLocationPoints()
        else if (tabs.value[selectedTab.value].value === 'restricted_location') await loadRestrictedLocations()
    }

    async function loadCountries(): Promise<void> {
        isLoading.value = true

        try {
            tableItems.value = (await app.$api.locations.get({limitCountries: -1, orderBy: 'ALPHABET'})).countries
        } catch (e) {
            app.$toast.error(t('load_error'))
        } finally {
            isLoading.value = false
        }
    }

    async function loadCities(): Promise<void> {
        if (!filters.value?.country?.id) return

        isLoading.value = true
        const rq = {limitCities: -1, orderBy: 'ALPHABET'}
        if (filters.value.country?.id) rq.countryId = filters.value.country.id

        try {
            tableItems.value = (await app.$api.locations.get(rq)).cities
        } catch (e) {
            app.$toast.error(t('load_error'))
        } finally {
            isLoading.value = false
        }
    }

    async function loadLocationPoints(): Promise<void> {
        const rq = {
            locale: 'all',
            ...(filters.value?.name && {namePattern: filters.value.name}),
            ...(filters.value.city?.id && {cityId: filters.value.city.id}),
            ...(filters.value.country?.id && {countryId: filters.value.country.id}),
            active: filters.value.active,
        }

        try {
            isLoading.value = true
            const res = await app.$api.locationPoints.get(rq)
            tableItems.value = res.locationPoints
        } catch (e) {
            app.$toast.error(t('load_error'))
        } finally {
            isLoading.value = false
        }
    }

    async function loadRestrictedLocations(): Promise<void> {
        isLoading.value = true

        try {
            const res = await app.$api.restrictedLocations.get()

            tableItems.value = res.restrictedLocations?.length ? res.restrictedLocations : []
            regionFilter.value = res.regionFilter
        } catch (e) {
            app.$toast.error(t('load_error'))
        } finally {
            isLoading.value = false
        }
    }

    // async function saveRestrictedLocation(): Promise<void> {
    //     if (!tableItems.value?.length) return
    //     const rq = {
    //         regionFilter: regionFilter.value,
    //         restrictedLocations: [],
    //         ...(regionFilter.value !== 'ALL' && {restrictedLocations: tableItems.value}),
    //     }
    //
    //     try {
    //         isSaving.value = true
    //         await app.$api.restrictedLocations.post(rq)
    //     } catch (e) {
    //         app.$toast.error(t('data_save_fail'))
    //     } finally {
    //         isSaving.value = false
    //     }
    // }
    //
    // function addToTable(item: object): void {
    //     tableItems.value.push(item)
    // }
    //
    // function removeFromTable(index: number): void {
    //     tableItems.value.splice(index, 1)
    // }
</script>
